/**
 * Impact/severity level of a status message.
 * `None` (empty string) represents "no/unknown severity".
 */
export const Severity = {
  None: "",
  Impact: "impact",
  Informational: "informational",
  NonImpact: "non-impact",
} as const;

export type Severity = (typeof Severity)[keyof typeof Severity];

/** All severity values, including the empty {@link Severity.None} option. */
export const SEVERITY_VALUES = Object.values(Severity);

/** Concrete impact levels (excludes the empty {@link Severity.None}). */
export const IMPACT_LEVELS: Severity[] = [
  Severity.Impact,
  Severity.Informational,
  Severity.NonImpact,
];

/** Type guard: whether the value is a known {@link Severity} (including none). */
export const isSeverity = (value: string): value is Severity =>
  (SEVERITY_VALUES as string[]).includes(value);

/** Type guard: whether the value is a concrete impact level (excludes none). */
export const isImpactLevel = (value: string): value is Severity =>
  (IMPACT_LEVELS as string[]).includes(value);

/**
 * Error categories reported by the components.
 * `None` (empty string) is the "no error" sentinel stored in the error state.
 */
export const ErrorType = {
  None: "",
  Socket: "Socket",
  API: "API",
  Token: "Token",
  Controller: "Controller",
  Sensecloud: "Sensecloud",
} as const;

export type ErrorType = (typeof ErrorType)[keyof typeof ErrorType];

/**
 * Object type derived from the access token. Values are the domain names used
 * throughout the components.
 */
export const ObjectType = {
  Internal: "internal",
  Installation: "installation",
  External: "external",
} as const;

export type ObjectType = (typeof ObjectType)[keyof typeof ObjectType];

/**
 * The short `otype` codes as they appear in the JWT.
 */
export const TokenOtype = {
  Internal: "int",
  Installation: "inst",
  External: "ext",
} as const;

export type TokenOtype = (typeof TokenOtype)[keyof typeof TokenOtype];

/**
 * Maps the short `otype` codes from the JWT to the domain {@link ObjectType}.
 * Typed as `Record<string, ObjectType>` so an unknown code maps to `undefined`
 * (matching the previous switch behaviour).
 */
export const TOKEN_OTYPE_TO_OBJECT_TYPE: Record<string, ObjectType> = {
  [TokenOtype.Internal]: ObjectType.Internal,
  [TokenOtype.Installation]: ObjectType.Installation,
  [TokenOtype.External]: ObjectType.External,
};

/**
 * The "active" flag of an encoded status message code (position 3 of the
 * `identifier:timestamp:active` triplet).
 */
export const StatusCodeState = {
  Inactive: 0,
  Active: 1,
} as const;

export type StatusCodeState =
  (typeof StatusCodeState)[keyof typeof StatusCodeState];

/**
 * Selects which label set a status item renders.
 */
export const LabelType = {
  Default: "default",
  Short: "short",
} as const;

export type LabelType = (typeof LabelType)[keyof typeof LabelType];

/**
 * UI-only elevator animation direction used by the 3D scene. Distinct from the
 * API {@link MovementState} (`"U"`/`"D"`) in `sensors.ts`.
 */
export const ElevatorDirection = {
  Up: "up",
  Down: "down",
} as const;

export type ElevatorDirection =
  (typeof ElevatorDirection)[keyof typeof ElevatorDirection];
